import { useMemo } from 'react';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FacultySchema, FacultyYearStatsSchema } from '../../../services/facultyApi';
import { z } from 'zod';
import { useAppSelector } from '../../../store/hooks';
import { MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../../utils/responsive';

type Faculty = z.infer<typeof FacultySchema>;

interface UseFacultiesColumnsProps {
	facultyStats?: { stats: z.infer<typeof FacultyYearStatsSchema>[] };
	isAdmin: boolean;
	isMobile?: boolean;
	onEdit: (faculty: Faculty) => () => void;
	onDelete: (faculty: Faculty) => () => void;
}

export function useFacultiesColumns({ isAdmin, facultyStats, isMobile = false, onEdit, onDelete }: UseFacultiesColumnsProps) {
	const years = useAppSelector((state) => state.filters.years);

	return useMemo<GridColDef[]>(
		() => {
			// Mobile: condensed single column with code, name, and latest year
			if (isMobile) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const latestYear = sortedYears[sortedYears.length - 1];
				const facultyStatsMap = new Map<string, Map<string, number>>();

				if (facultyStats?.stats) {
					facultyStats.stats.forEach((facultyStat) => {
						if (!facultyStatsMap.has(facultyStat.maKhoa)) {
							facultyStatsMap.set(facultyStat.maKhoa, new Map());
						}
						const yearDataMap = facultyStatsMap.get(facultyStat.maKhoa)!;
						Object.entries(facultyStat.yearData).forEach(([year, count]) => {
							yearDataMap.set(year, count as number);
						});
					});
				}

				return [
					{
						field: 'tenKhoa',
						headerName: 'Khoa',
						minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
						flex: 1,
						sortable: true,
						filterable: false,
						renderCell: ({ row }) => {
							const yearDataMap = facultyStatsMap.get(row.maKhoa);
							const latestCount = latestYear && yearDataMap ? yearDataMap.get(latestYear.toString()) || 0 : 0;
							return (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										padding: MOBILE_CELL_STYLES.PADDING,
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
										gap: MOBILE_CELL_STYLES.GAP,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{row.maKhoa || 'N/A'}
									</Typography>
									<Typography variant="body2" fontWeight="bold">
										{row.tenKhoa || 'N/A'}
									</Typography>
									{latestYear && (
										<Typography variant="caption" color="text.secondary">
											{latestYear}: {latestCount} sinh viên
										</Typography>
									)}
									<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
										{MOBILE_GUIDE.TEXT}
									</Typography>
								</Box>
							);
						},
					},
				];
			}

			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const currentPage = api.state.pagination.paginationModel.page;
						const pageSize = api.state.pagination.paginationModel.pageSize;
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						const stt = currentPage * pageSize + rowIndex + 1;
						return stt;
					},
				},
				{
					field: 'maKhoa',
					headerName: 'Mã Khoa',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: false
				},
				{
					field: 'tenKhoa',
					headerName: 'Tên Khoa',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false
				},
			];

			// Add dynamic year columns based on new stats structure
			const yearColumns: GridColDef[] = [];
			if (years.length > 0 && facultyStats?.stats) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const facultyStatsMap = new Map<string, Map<string, number>>();

				// Create a map for easy lookup: maKhoa -> year -> count
				facultyStats.stats.forEach((facultyStat) => {
					if (!facultyStatsMap.has(facultyStat.maKhoa)) {
						facultyStatsMap.set(facultyStat.maKhoa, new Map());
					}
					const yearDataMap = facultyStatsMap.get(facultyStat.maKhoa)!;
					Object.entries(facultyStat.yearData).forEach(([year, count]) => {
						yearDataMap.set(year, count as number);
					});
				});

				sortedYears.slice(-5).forEach((year: number) => {
					yearColumns.push({
						field: year.toString(),
						headerName: year.toString(),
						width: 80,
						type: 'number',
						headerAlign: 'center',
						sortable: true,
						filterable: false,
						align: 'center',
						renderCell: ({ row }: { row: Faculty }) => {
							const yearDataMap = facultyStatsMap.get(row.maKhoa);
							const count = yearDataMap ? yearDataMap.get(year.toString()) || 0 : 0;
							return count;
						},
					});
				});
			}

			const actionColumns: GridColDef[] = isAdmin ? [{
				field: 'actions' as const,
				type: 'actions' as const,
				headerName: 'Thao tác',
				width: 120,
				headerAlign: 'center' as const,
				getActions: ({ row }: { row: Faculty }) => [
					<GridActionsCellItem
						key="edit-item"
						icon={<EditIcon />}
						label="Chỉnh sửa"
						onClick={onEdit(row)}
						color="primary"
					/>,
					<GridActionsCellItem
						key="delete-item"
						icon={<DeleteIcon />}
						label="Xóa"
						onClick={onDelete(row)}
						color="error"
					/>,
				],
			}] : [];

			return [...baseColumns, ...yearColumns, ...actionColumns];
		},
		[isAdmin, years, facultyStats, isMobile, onEdit, onDelete],
	);
}